import { useState, useEffect } from 'react';
import { AlertTriangle, X, Check, Info } from 'lucide-react';
import { api } from '../api';
import FilterBar from '../components/FilterBar';
import ProjectRateCardPanel from '../components/ProjectRateCardPanel';
import ProjectEarnedValuePanel from '../components/ProjectEarnedValuePanel';
import { SourceNote } from '../components/ui';
import { BillingEntityFilter, BillingEntityBadge } from '../components/BillingEntity';

const MARGIN_SOURCES = [
  { source: 'Magnetic', label: 'Revenue / commercial value', detail: 'project once-off + monthly retainer amounts' },
  { source: 'Magnetic', label: 'Tracked hours', detail: 'logged time per project' },
  { source: 'BambooHR', label: 'Cost-to-company', detail: 'monthly salary → hourly cost rate (÷160). Flagged when estimated.' },
  { source: 'App', label: 'Rate card / overhead', detail: 'pinned rate card per project + overhead pool' },
  { source: 'Derived', label: 'Net margin', detail: 'revenue − (hours × cost rate + overhead), via the active formula graph' },
];

const R   = v => `R ${Number(v || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
const PCT = v => `${Number(v || 0).toFixed(1)}%`;
const H   = v => `${Number(v || 0).toFixed(1)}h`;

function profitColor(val) {
  return (val || 0) >= 0 ? 'text-emerald-600' : 'text-brand-500';
}
function marginColor(val) {
  return (val || 0) >= 0 ? 'text-emerald-600' : 'text-brand-500';
}
function Tip({ children, tip }) {
  return (
    <span className="relative group inline-flex items-center gap-1">
      {children}
      <Info className="w-3 h-3 text-slate-500 group-hover:text-slate-300 transition-colors" strokeWidth={2} />
      <span className="pointer-events-none absolute left-0 bottom-full mb-2 z-50 w-52 rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-200 leading-relaxed shadow-xl opacity-0 group-hover:opacity-100 transition-opacity">
        {tip}
      </span>
    </span>
  );
}

function StatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  const isActive = s === 'ACTIVE';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      isActive ? 'bg-emerald-500/15 text-emerald-600' : 'bg-slate-700/60 text-slate-400'
    }`}>
      {isActive ? 'Active' : (status || 'Unknown')}
    </span>
  );
}

export default function ProjectMargins() {
  const [view, setView]         = useState('project'); // 'month' | 'project'
  const [months, setMonths]     = useState([]);
  const [month, setMonth]       = useState(null);
  const [projects, setProjects] = useState([]);
  const [totals, setTotals]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [audit, setAudit]       = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [search, setSearch]     = useState('');
  const [entity, setEntity]     = useState('');   // billing entity id filter ('' = all)
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.kpi.months().then(data => {
      setMonths(data);
      if (data.length) setMonth(data[0].period_month);
    });
    api.projects.totals().then(setTotals);
  }, []);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    api.projects.list({ month }).then(data => {
      setProjects(data);
      setLoading(false);
    });
  }, [month]);

  useEffect(() => {
    if (!selected) { setDetail(null); setAudit(null); setShowAudit(false); return; }
    const m = view === 'month' ? month : undefined;
    api.projects.detail(selected.project_id, m).then(setDetail);
    api.projects.assignments(selected.project_id).then(setAudit);
  }, [selected, month, view]);

  // Numeric monthly revenue: once-off spread evenly, retainer/T&M as stored.
  const historyRevenueNum = (h) => {
    if (h.is_unbilled) return 0;
    if (selected?.once_off_amount > 0 && detail?.history?.length)
      return selected.once_off_amount / detail.history.length;
    return h.commercial_value || 0;
  };
  const historyRevenue = (h) => {
    if (h.is_unbilled) return <span className="text-brand-500">Unbilled</span>;
    return R(historyRevenueNum(h));
  };
  const historyNetProfit = (h) => {
    const cost = (h.total_billing_value || 0) * 0.33;
    return historyRevenueNum(h) - cost;
  };
  const historyMarginPct = (h) => {
    const rev = historyRevenueNum(h);
    if (!rev) return null;
    return (historyNetProfit(h) / rev) * 100;
  };

  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const MONTH_COLS = [
    { label: 'Project',         key: 'project_name',             type: 'str' },
    { label: 'Client',          key: 'client_name',              type: 'str' },
    { label: 'Entity',          key: 'billing_entity_name',      type: 'str' },
    { label: 'Status',          key: 'status',                   type: 'str' },
    { label: 'Once-off',        key: 'once_off_amount',          type: 'num' },
    { label: 'Retainer/mo',     key: 'monthly_amount',           type: 'num' },
    { label: 'Revenue',         key: 'commercial_value',         type: 'num' },
    { label: 'Tracked Billing', key: 'total_billing_value',      type: 'num' },
    { label: 'Est. Hours',      key: 'estimated_hours_snapshot', type: 'num' },
    { label: 'Tracked Hours',   key: 'total_tracked_hours',      type: 'num' },
    { label: 'Net Profit',      key: 'net_profit',               type: 'num' },
    { label: 'Margin %',        key: 'net_margin_pct',           type: 'num' },
  ];
  const PROJECT_COLS = [
    { label: 'Project',         key: 'project_name',          type: 'str' },
    { label: 'Client',          key: 'client_name',           type: 'str' },
    { label: 'Entity',          key: 'billing_entity_name',   type: 'str' },
    { label: 'Status',          key: 'status',                type: 'str' },
    { label: 'Once-off',        key: 'once_off_amount',       type: 'num' },
    { label: 'Retainer/mo',     key: 'monthly_amount',        type: 'num' },
    { label: 'Months',          key: 'months_active',         type: 'num' },
    { label: 'Total Revenue',   key: 'total_revenue',         type: 'num' },
    { label: 'Total Billing',   key: 'total_billing_value',   type: 'num' },
    { label: 'Est. Hours',      key: 'estimated_hours',       type: 'num' },
    { label: 'Tracked Hours',   key: 'total_tracked_hours',   type: 'num' },
    { label: 'Total Profit',    key: 'total_net_profit',      type: 'num' },
    { label: 'Avg Margin',      key: 'avg_net_margin_pct',    type: 'num' },
  ];
  const cols = view === 'month' ? MONTH_COLS : PROJECT_COLS;

  const rows = view === 'month' ? projects : totals;
  const filtered = rows
    .filter(r => !entity || String(r.billing_entity_id) === String(entity))
    .filter(r =>
      !search ||
      (r.project_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.client_name  || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortCol) return 0;
      const col = cols.find(c => c.key === sortCol);
      const av = a[sortCol] ?? (col?.type === 'str' ? '' : -Infinity);
      const bv = b[sortCol] ?? (col?.type === 'str' ? '' : -Infinity);
      const cmp = col?.type === 'str'
        ? av.toString().localeCompare(bv.toString())
        : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const viewToggle = (
    <div className="flex bg-slate-800 border border-slate-700 rounded-md p-0.5 gap-0.5">
      {[['month', 'By month'], ['project', 'By project']].map(([v, label]) => (
        <button
          key={v}
          onClick={() => { setView(v); setSelected(null); }}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            view === v ? 'bg-brand-500 text-white' : 'text-slate-500 hover:text-slate-100'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <header className="flex items-end justify-between gap-6 mb-8 pb-6 border-b border-slate-700">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Profitability</p>
          <h1 className="text-3xl font-semibold text-slate-100">Project margins</h1>
          <p className="text-sm text-slate-400 mt-2">
            Drill into per-project revenue, cost and net margin across the {view === 'month' ? 'selected month' : 'project lifetime'}.
          </p>
        </div>
        {viewToggle}
      </header>

      <div className="flex flex-wrap items-end gap-4">
        <FilterBar
          months={view === 'month' ? months : undefined}
          month={month}
          onMonth={m => { setMonth(m); setSelected(null); }}
          search={search}
          onSearch={setSearch}
        />
        <BillingEntityFilter value={entity} onChange={(v) => { setEntity(v); setSelected(null); }} />
      </div>

      <SourceNote items={MARGIN_SOURCES} className="mt-6" />

      <div className="flex flex-col gap-6">

        {/* Detail panel — above the list */}
        {selected ? (
          <div className="bg-slate-800 rounded-xl p-5 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-200">{selected.project_name}</h2>
                <p className="text-sm text-slate-400">
                  {selected.client_name || '—'} · {view === 'month' ? month : 'All time'}
                </p>
              </div>
              <button onClick={() => setSelected(null)} aria-label="Close" className="text-slate-500 hover:text-slate-100 transition-colors p-1 -mr-1">
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>

            {/* By Month: single-month profitability breakdown */}
            {view === 'month' && (
              <>
                {selected.is_unbilled && (
                  <div className="bg-brand-500/10 border border-brand-500/30 rounded-md px-4 py-3 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" strokeWidth={2} />
                    <p className="text-brand-500 text-sm font-medium">No revenue set — cost is accumulating but margin cannot be calculated.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    {[
                      ['Revenue',          R(selected.commercial_value),                'text-slate-200', 'Retainer = monthly fee. Once-off = contract amount. T&M = hours × sale rate.'],
                      ['Tracked Billing',  R(selected.total_billing_value),             'text-slate-300', 'Total hours tracked this month × each dev\'s sale rate from Magnetic.'],
                      ['Cost Price (33%)', `− ${R(selected.total_billing_value * 0.33)}`, 'text-amber-600', 'Tracked Billing × 33% — dev cost at one-third of their sale rate.'],
                      ['Net Profit',       R(selected.net_profit),                      profitColor(selected.net_profit), 'Revenue − Cost Price.'],
                    ].map(([label, val, cls, tip]) => (
                      <div key={label} className="flex justify-between text-sm border-b border-slate-700 pb-2">
                        <span className="text-slate-400"><Tip tip={tip}>{label}</Tip></span>
                        <span className={`font-medium ${cls}`}>{val}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-1">
                      <span className="text-slate-400">
                        <Tip tip="Net Profit ÷ Revenue × 100.">Net Margin</Tip>
                      </span>
                      <span className={`font-bold text-base ${selected.is_unbilled ? 'text-slate-500' : marginColor(selected.net_margin_pct)}`}>
                        {selected.is_unbilled ? '—' : PCT(selected.net_margin_pct)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-slate-700 pt-3 mt-1">
                      <span className="text-slate-400">
                        <Tip tip="Scoped hours from the project brief in Magnetic.">Est. Hours</Tip>
                      </span>
                      <span className="font-medium text-slate-300">{H(selected.estimated_hours_snapshot)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-b border-slate-700 pb-2">
                      <span className="text-slate-400">
                        <Tip tip="Total hours logged in Magnetic for this month, all devs combined — including unmatched users with no BambooHR record.">Tracked Hours</Tip>
                      </span>
                      <span className="font-medium text-slate-300">{H(selected.total_tracked_hours)}</span>
                    </div>
                  </div>

                  {detail?.devs?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Developers this month</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-500 border-b border-slate-700">
                            {['Name', 'Role', 'Billable', 'Non-Bill.', 'Total'].map(h => (
                              <th key={h} className="text-left pb-2 pr-3 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detail.devs.map(d => (
                            <tr key={d.employee_id || d.full_name} className="border-b border-slate-700/40">
                              <td className="py-1.5 pr-3 text-slate-200">{d.full_name}</td>
                              <td className="py-1.5 pr-3 text-slate-400">{d.role || '—'}</td>
                              <td className="py-1.5 pr-3 text-slate-300">{H(d.billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-400">{H(d.non_billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-200 font-medium">{H(d.tracked_hours)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* By Project: lifetime aggregate */}
            {view === 'project' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  {[
                    ['Total Revenue',    R(selected.total_revenue),                                     'text-slate-200',  'Retainer = sum of monthly fees. Once-off = contract amount (counted once). T&M = sum of all billing values.'],
                    ['Total Billing',    R(selected.total_billing_value),                               'text-slate-300',  'Sum of all hours tracked × each dev\'s sale rate, across all months.'],
                    ['Cost Price (33%)', `− ${R((selected.total_billing_value || 0) * 0.33)}`,         'text-amber-600',  'Total Billing × 33% — approximates total dev cost at one-third of the sale rate.'],
                    ['Net Profit',       R(selected.total_net_profit),                                  profitColor(selected.total_net_profit), 'Total Revenue − Cost Price.'],
                    ['Months Active',    `${selected.months_active || 0} months`,                       'text-slate-300',  'Number of calendar months with logged time entries.'],
                  ].map(([label, val, cls, tip]) => (
                    <div key={label} className="flex justify-between text-sm border-b border-slate-700 pb-2">
                      <span className="text-slate-400"><Tip tip={tip}>{label}</Tip></span>
                      <span className={`font-medium ${cls}`}>{val}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-1">
                    <span className="text-slate-400">
                      <Tip tip="Net Profit ÷ Total Revenue × 100, across the full project lifetime.">Net Margin</Tip>
                    </span>
                    <span className={`font-bold text-base ${selected.avg_net_margin_pct != null ? marginColor(selected.avg_net_margin_pct) : 'text-slate-500'}`}>
                      {selected.avg_net_margin_pct != null ? PCT(selected.avg_net_margin_pct) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-slate-700 pt-3 mt-1">
                    <span className="text-slate-400">
                      <Tip tip="Scoped hours from the project brief in Magnetic.">Est. Hours</Tip>
                    </span>
                    <span className="font-medium text-slate-300">{H(selected.estimated_hours)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-slate-700 pb-2">
                    <span className="text-slate-400">
                      <Tip tip="Total hours logged in Magnetic across all months, all devs combined — including unmatched users with no BambooHR record.">Total Tracked Hours</Tip>
                    </span>
                    <span className="font-medium text-slate-300">{H(selected.total_tracked_hours)}</span>
                  </div>
                </div>

                {detail?.history?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Monthly History</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-700">
                          {['Month', 'Revenue', 'Net Profit', 'Margin %', 'Devs'].map(h => (
                            <th key={h} className="text-left pb-2 pr-3 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.history.map(h => {
                          const np  = historyNetProfit(h);
                          const pct = historyMarginPct(h);
                          return (
                            <tr key={h.period_month} className="border-b border-slate-700/40">
                              <td className="py-1.5 pr-3 text-slate-300 font-medium">{h.period_month}</td>
                              <td className="py-1.5 pr-3 text-slate-300">{historyRevenue(h)}</td>
                              <td className={`py-1.5 pr-3 font-medium ${profitColor(np)}`}>{h.is_unbilled ? '—' : R(np)}</td>
                              <td className={`py-1.5 pr-3 ${h.is_unbilled || pct == null ? 'text-slate-500' : marginColor(pct)}`}>
                                {h.is_unbilled || pct == null ? '—' : PCT(pct)}
                              </td>
                              <td className="py-1.5 pr-3 text-slate-400">{h.number_of_devs}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Monthly history for By Month mode */}
            {view === 'month' && detail?.history?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Monthly History</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700">
                      {['Month', 'Revenue', 'Net Profit', 'Margin %', 'Devs'].map(h => (
                        <th key={h} className="text-left pb-2 pr-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.history.map(h => {
                      const np  = historyNetProfit(h);
                      const pct = historyMarginPct(h);
                      return (
                        <tr key={h.period_month} className="border-b border-slate-700/40">
                          <td className="py-1.5 pr-3 text-slate-300 font-medium">{h.period_month}</td>
                          <td className="py-1.5 pr-3 text-slate-300">{historyRevenue(h)}</td>
                          <td className={`py-1.5 pr-3 font-medium ${profitColor(np)}`}>{h.is_unbilled ? '—' : R(np)}</td>
                          <td className={`py-1.5 pr-3 ${h.is_unbilled || pct == null ? 'text-slate-500' : marginColor(pct)}`}>
                            {h.is_unbilled || pct == null ? '—' : PCT(pct)}
                          </td>
                          <td className="py-1.5 pr-3 text-slate-400">{h.number_of_devs}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Hours Audit drill-down */}
            {audit && (
              <div>
                <button
                  onClick={() => setShowAudit(v => !v)}
                  className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-widest hover:text-slate-200 transition-colors"
                >
                  <span>{showAudit ? '▾' : '▸'}</span>
                  Hours Audit
                  <span className="normal-case tracking-normal font-normal text-slate-500 ml-1">
                    {H(audit.summary.total_hours)} total
                    {audit.summary.unmatched_hours > 0 &&
                      ` · ${H(audit.summary.unmatched_hours)} unmatched (no BambooHR record)`}
                  </span>
                </button>

                {showAudit && (
                  <div className="mt-3 space-y-4">
                    {/* Summary bar */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        ['Total in DB',   H(audit.summary.total_hours),       'text-slate-200'],
                        ['Billable',      H(audit.summary.billable_hours),     'text-emerald-600'],
                        ['Non-billable',  H(audit.summary.non_billable_hours), 'text-slate-400'],
                        ['Unmatched',     H(audit.summary.unmatched_hours),    audit.summary.unmatched_hours > 0 ? 'text-amber-600' : 'text-slate-500'],
                      ].map(([label, val, cls]) => (
                        <div key={label} className="bg-slate-900/50 rounded-lg px-3 py-2">
                          <p className="text-xs text-slate-500 mb-0.5">{label}</p>
                          <p className={`text-sm font-bold ${cls}`}>{val}</p>
                        </div>
                      ))}
                    </div>

                    {/* By person */}
                    <div>
                      <p className="text-xs text-slate-500 mb-2">By person</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-500 border-b border-slate-700">
                            {['Name', 'Email', 'Billable', 'Non-Bill.', 'Total', 'BambooHR'].map(h => (
                              <th key={h} className="text-left pb-2 pr-3 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {audit.by_person.map(p => (
                            <tr key={p.work_email} className={`border-b border-slate-700/40 ${!p.employee_id ? 'opacity-60' : ''}`}>
                              <td className="py-1.5 pr-3 text-slate-200">{p.full_name}</td>
                              <td className="py-1.5 pr-3 text-slate-500 text-[10px]">{p.work_email}</td>
                              <td className="py-1.5 pr-3 text-emerald-600">{H(p.billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-400">{H(p.non_billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-200 font-medium">{H(p.total_hours)}</td>
                              <td className="py-1.5 pr-3">
                                {p.employee_id
                                  ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="w-3 h-3" strokeWidth={2.5} /> Matched</span>
                                  : <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" strokeWidth={2} /> No record</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {audit.summary.unmatched_hours > 0 && (
                        <p className="text-[10px] text-slate-500 mt-2">~ Unmatched users have no BambooHR record but their hours and billing value are still included in profitability calculations.</p>
                      )}
                    </div>

                    {/* By week */}
                    <details className="bg-slate-900/30 rounded-lg p-3">
                      <summary className="text-xs text-slate-400 cursor-pointer font-medium">Weekly breakdown ({audit.by_week.length} weeks)</summary>
                      <table className="w-full text-xs mt-3">
                        <thead>
                          <tr className="text-slate-500 border-b border-slate-700">
                            {['Week commencing', 'Billable', 'Non-Bill.', 'Total', 'People'].map(h => (
                              <th key={h} className="text-left pb-2 pr-3 font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {audit.by_week.map(w => (
                            <tr key={w.week_commencing} className="border-b border-slate-700/40">
                              <td className="py-1.5 pr-3 text-slate-300">{w.week_commencing}</td>
                              <td className="py-1.5 pr-3 text-emerald-600">{H(w.billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-400">{H(w.non_billable_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-200 font-medium">{H(w.total_hours)}</td>
                              <td className="py-1.5 pr-3 text-slate-400">{w.people}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  </div>
                )}
              </div>
            )}

            <ProjectEarnedValuePanel projectId={selected.project_id} />
            <ProjectRateCardPanel projectId={selected.project_id} />
          </div>
        ) : null}

        {/* Project list */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-700 bg-slate-900 sticky top-0 z-10">
                  {cols.map(({ label, key }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="text-left px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-200 transition-colors"
                    >
                      <span className="flex items-center gap-1">
                        {label}
                        <span className="text-slate-600">
                          {sortCol === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && view === 'month' && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
                )}
                {filtered.map(r => (
                  <tr
                    key={r.project_id}
                    onClick={() => setSelected(selected?.project_id === r.project_id ? null : r)}
                    className={`border-b border-slate-700/50 cursor-pointer transition-colors ${
                      selected?.project_id === r.project_id ? 'bg-brand-700/20' : 'hover:bg-slate-700/30'
                    }`}
                  >
                    <td className="px-4 py-2.5 text-slate-200 font-medium max-w-[160px] truncate">{r.project_name}</td>
                    <td className="px-4 py-2.5 text-slate-400 max-w-[100px] truncate">{r.client_name || '—'}</td>
                    <td className="px-4 py-2.5"><BillingEntityBadge name={r.billing_entity_name} /></td>
                    {view === 'month' ? (
                      <>
                        <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-2.5 text-slate-300">{r.once_off_amount > 0 ? R(r.once_off_amount) : '—'}</td>
                        <td className="px-4 py-2.5 text-slate-300">{r.monthly_amount > 0 ? R(r.monthly_amount) : '—'}</td>
                        <td className="px-4 py-2.5 text-slate-200">
                          {r.is_unbilled ? <span className="text-brand-500 text-xs">Unbilled</span> : R(r.commercial_value)}
                        </td>
                        <td className="px-4 py-2.5 text-slate-300">{R(r.total_billing_value)}</td>
                        <td className="px-4 py-2.5 text-slate-300">{H(r.estimated_hours_snapshot)}</td>
                        <td className="px-4 py-2.5 text-slate-300">{H(r.total_tracked_hours)}</td>
                        <td className={`px-4 py-2.5 font-medium ${profitColor(r.net_profit)}`}>{R(r.net_profit)}</td>
                        <td className={`px-4 py-2.5 font-medium ${r.is_unbilled ? 'text-slate-500' : marginColor(r.net_margin_pct)}`}>
                          {r.is_unbilled ? '—' : PCT(r.net_margin_pct)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-2.5 text-slate-300">{r.once_off_amount > 0 ? R(r.once_off_amount) : '—'}</td>
                        <td className="px-4 py-2.5 text-slate-300">{r.monthly_amount > 0 ? R(r.monthly_amount) : '—'}</td>
                        <td className="px-4 py-2.5 text-slate-400">{r.months_active || 0}</td>
                        <td className="px-4 py-2.5 text-slate-200">{R(r.total_revenue)}</td>
                        <td className="px-4 py-2.5 text-slate-300">{R(r.total_billing_value)}</td>
                        <td className="px-4 py-2.5 text-slate-300">{H(r.estimated_hours)}</td>
                        <td className="px-4 py-2.5 text-slate-300">{H(r.total_tracked_hours)}</td>
                        <td className={`px-4 py-2.5 font-medium ${profitColor(r.total_net_profit)}`}>{R(r.total_net_profit)}</td>
                        <td className={`px-4 py-2.5 font-medium ${r.avg_net_margin_pct != null ? marginColor(r.avg_net_margin_pct) : 'text-slate-500'}`}>
                          {r.avg_net_margin_pct != null ? PCT(r.avg_net_margin_pct) : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-8 text-center text-slate-500">No projects</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
